import { Navigate } from 'react-router-dom'
import { useAuth } from './AuthProvider'

interface PortalRouteProps {
  children: React.ReactNode
}

const QA_BYPASS = import.meta.env.VITE_QA_BYPASS === 'true'

/**
 * SPEC E4 R1 — guard for the `/portal/*` route tree. Mirrors ProtectedRoute but
 * is for CLIENT-role users ONLY:
 *   - unauthenticated  → redirect to /login
 *   - role === 'client' → render the portal (children)
 *   - any other authed role (admin/user/viewer) → redirect to / (the operator UI)
 *
 * The complementary direction (a CLIENT user who lands on an OPERATOR route is
 * sent to /portal) is handled by ProtectedRoute via the OperatorRedirect guard
 * in App.tsx, so the two guards together make the portal and the operator UI
 * mutually exclusive per role.
 */
export const PortalRoute = ({ children }: PortalRouteProps) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (QA_BYPASS) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Authenticated but NOT a client → turn away to the operator UI.
  if (user?.role !== 'client') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
